"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useAuth0 } from "@auth0/auth0-react";
import { OutlineDisplay } from "./ornaments";


export function CharacterConstellation() {
  return (
    <section
      style={{
        padding: "120px 40px",
        background: "#0E0F22",
        color: "#F8F1DF",
        borderTop: "1.5px solid var(--ink)",
        borderBottom: "1.5px solid var(--ink)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 0.6px, transparent 1px)",
          backgroundSize: "5px 5px",
          opacity: 0.08,
        }}
      />
      <div style={{ maxWidth: 1200, margin: "0 auto 40px", position: "relative" }}>
        <div className="caps" style={{ color: "var(--lime)" }}>the book's brain · feature nº 05</div>
        <h2
          className="tanker"
          style={{
            fontSize: "clamp(52px, 6.6vw, 118px)",
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            margin: "12px 0 10px",
          }}
        >
          ask it anything. <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>it already knows</span>.
        </h2>
        <p className="display" style={{ fontStyle: "italic", fontSize: 21, maxWidth: 680, opacity: 0.8, margin: 0 }}>
          quarkle re-reads your chapters and notes before it answers: characters, world, and plot details it keeps updated as you write. this is the real Quarkle Studio, mid-conversation.
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <figure style={{ margin: 0 }}>
          <div
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 20px 50px -20px rgba(0,0,0,0.6)",
            }}
          >
            <Image
              src="/images/app/editor-overview.png"
              alt="Quarkle Studio: the manuscript editor next to Quarkle Chat, answering a question about a character with details drawn from the book"
              width={3288}
              height={1934}
              sizes="(max-width: 760px) calc(100vw - 36px), (max-width: 1400px) calc(100vw - 80px), 1100px"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          </div>
          <figcaption
            className="mono"
            style={{ fontSize: 11, opacity: 0.6, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 10, textAlign: "center" }}
          >
            the manuscript, and quarkle chat answering from it. a real screenshot, not a mockup
          </figcaption>
        </figure>
      </div>

      <div
        className="qv6-brain-facts"
        style={{
          maxWidth: 1200,
          margin: "40px auto 0",
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 20,
        }}
      >
        {[
          ["auto", "characters & world notes, discovered as you write"],
          ["frozen", "note pins canon exactly as you wrote it"],
          ["versioned", "saved changes stay available in revision history"],
        ].map(([n, l], i) => (
          <div
            key={i}
            style={{
              borderTop: "1px solid rgba(255,255,255,0.2)",
              paddingTop: 14,
              display: "flex",
              alignItems: "baseline",
              gap: 14,
            }}
          >
            <span
              className="tanker"
              style={{ fontSize: 40, fontStyle: "italic", color: "var(--lime)", lineHeight: 0.85 }}
            >
              {n}
            </span>
            <span className="display" style={{ fontStyle: "italic", fontSize: 17, opacity: 0.8 }}>
              {l}
            </span>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 1200, margin: "36px auto 0", textAlign: "center" }}>
        <a
          href="/memory"
          className="display"
          style={{
            fontStyle: "italic",
            fontSize: 19,
            color: "var(--lime)",
            textDecoration: "none",
            borderBottom: "1.5px solid var(--lime)",
            paddingBottom: 2,
          }}
        >
          see how quarkle remembers your book →
        </a>
      </div>
    </section>
  );
}

export function WaxSealCTA() {
  const [stamped, setStamped] = useState(false);
  const { loginWithRedirect } = useAuth0();
  const startSignup = () =>
    loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });

  /* When the letter gets sealed, wait 1.5s — let the user see the stamp — then go to signup. */
  useEffect(() => {
    if (!stamped) return;
    const t = setTimeout(startSignup, 1500);
    return () => clearTimeout(t);
  }, [stamped]);

  return (
    <section
      style={{
        padding: "140px 40px 120px",
        background: "#0E0F22",
        color: "#F8F1DF",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 40,
          left: "50%",
          transform: "translateX(-50%)",
          opacity: 0.08,
          lineHeight: 0.8,
          pointerEvents: "none",
        }}
      >
        <OutlineDisplay text="sincerely," size={320} color="#F8F1DF" strokeW={2} />
      </div>

      <div style={{ position: "relative", maxWidth: 820, margin: "0 auto" }}>
        <span className="caps" style={{ color: "var(--pink)" }}>a letter, to you · from us</span>
        <h2
          className="tanker"
          style={{
            fontSize: "clamp(56px, 7vw, 128px)",
            lineHeight: 0.94,
            letterSpacing: "-0.035em",
            margin: "14px 0 36px",
          }}
        >
          press the seal.
          <br />
          <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>
            {stamped ? "see you in the document." : "start the book."}
          </span>
        </h2>

        <div
          className="qv6-waxseal-letter"
          style={{
            position: "relative",
            background: "#F8F1DF",
            color: "#1A1410",
            border: "1.5px solid #F8F1DF",
            borderRadius: 4,
            padding: "56px 60px 210px",
            maxWidth: 680,
            margin: "0 auto",
            boxShadow: "12px 12px 0 var(--pink)",
            textAlign: "left",
          }}
        >
          <div className="hand" style={{ fontSize: 22, color: "#1A1410", lineHeight: 1.25 }}>
            dear writer, <br />
            <br />
            the book is already inside you. we will not write it for you. we will sit beside you while you do. we will remember every page. we will not flinch at a first draft. we will be here tomorrow and the tomorrow after that. <br />
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;yours, quarkle
          </div>

          {/* Seal slot: a dotted placeholder until pressed, then a realistic wax seal drops in. */}
          <div
            className="qv6-waxseal-slot"
            style={{
              position: "absolute",
              bottom: 30,
              right: 56,
              width: 170,
              height: 170,
            }}
          >
            {!stamped && (
              <button
                className="qv6-seal-placeholder"
                onClick={() => setStamped(true)}
                aria-label="press here to seal the letter and start the book"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: 999,
                  border: "2.5px dashed rgba(20, 20, 45, 0.32)",
                  background: "transparent",
                  cursor: "pointer",
                  outline: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: 0,
                  color: "rgba(20, 20, 45, 0.55)",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  transition: "border-color 0.22s ease, color 0.22s ease, background 0.22s ease, transform 0.22s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(224, 24, 111, 0.6)";
                  e.currentTarget.style.color = "rgba(224, 24, 111, 0.85)";
                  e.currentTarget.style.background = "rgba(224, 24, 111, 0.04)";
                  e.currentTarget.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(20, 20, 45, 0.32)";
                  e.currentTarget.style.color = "rgba(20, 20, 45, 0.55)";
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M12 3v13" />
                  <path d="M7 8l5-5 5 5" />
                  <path d="M5 20h14" />
                </svg>
                <div style={{ textAlign: "center", lineHeight: 1.6 }}>
                  place<br />the seal<br />here
                </div>
              </button>
            )}

            {stamped && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  animation: "qv6-stamp-drop 0.75s cubic-bezier(0.2, 0.9, 0.3, 1.25) forwards",
                  transformOrigin: "center center",
                  pointerEvents: "none",
                }}
              >
                <RealisticWaxSeal />
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            marginTop: 60,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.7,
            color: "#F8F1DF",
          }}
        >
          {stamped ? "sealed · opening the editor in a second…" : "↑ press the seal · we'll sign you in from there"}
        </div>
      </div>
    </section>
  );
}

/* Realistic pressed-wax seal. Muted brick-red disc with a raised rim and
 * the Quarkle trio-of-orbs embossed into the wax. Built as layered SVG:
 *   - radial-gradient body + macro mottling + fine grain for the wax surface
 *   - inner recess shadow + rim stroke + thin top-left highlight arcs
 *   - orbs built from (cast shadow) + (offset highlight crescent) + (main fill)
 *   - engraved detail: dark recessed bars/waves with tiny opposite-edge highlight
 *   - upper-left soft sheen on top of everything
 * All colours sit in the same terracotta family — no black, no outlines. */
function RealisticWaxSeal() {
  const discPath =
    "M 110 14 C 160 14 206 58 206 108 " +
    "C 208 160 160 206 108 206 " +
    "C 58 208 14 160 14 110 " +
    "C 14 58 58 14 110 14 Z";

  const orbs = (
    <>
      <circle cx="62" cy="18" r="9" />
      <circle cx="20" cy="24" r="13" />
      <circle cx="46" cy="58" r="21" />
    </>
  );

  return (
    <svg
      viewBox="0 0 220 220"
      width="100%"
      height="100%"
      style={{
        filter:
          "drop-shadow(0 3px 7px rgba(36, 6, 4, 0.36)) drop-shadow(0 1px 1.5px rgba(0,0,0,0.22))",
      }}
    >
      <defs>
        {/* Fine matte grain — the powdery dried-wax finish. */}
        <filter id="wsGrain">
          <feTurbulence type="fractalNoise" baseFrequency="3.2" numOctaves="2" seed="5" />
          <feColorMatrix values="0 0 0 0 0.4   0 0 0 0 0.14   0 0 0 0 0.09   0 0 0 0.07 0" />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
        {/* Macro-scale colour mottling — gives the wax organic variation. */}
        <filter id="wsMacro">
          <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="1" seed="11" />
          <feColorMatrix values="0 0 0 0 0.32   0 0 0 0 0.07   0 0 0 0 0.04   0 0 0 0.045 0" />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
        {/* Cast shadow behind raised orbs. */}
        <filter id="wsShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.3" result="b" />
          <feOffset in="b" dx="0.7" dy="1.2" result="o" />
          <feFlood floodColor="#140303" floodOpacity="0.65" />
          <feComposite in2="o" operator="in" />
        </filter>
        {/* Tiny cast shadow for engraved detail lines — makes them read as recesses. */}
        <filter id="wsDetailShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.45" result="b" />
          <feOffset in="b" dx="0.35" dy="0.55" result="o" />
          <feFlood floodColor="#110202" floodOpacity="0.7" />
          <feComposite in2="o" operator="in" />
        </filter>

        {/* Disc body — muted brick red, gently shaded across the disc. */}
        <radialGradient id="wsBody" cx="40%" cy="30%" r="82%">
          <stop offset="0%" stopColor="#A83E2F" />
          <stop offset="45%" stopColor="#8F3024" />
          <stop offset="85%" stopColor="#641E19" />
          <stop offset="100%" stopColor="#3A0D0A" />
        </radialGradient>

        {/* Soft pearl sheen on the upper-left, matte rather than glossy. */}
        <radialGradient id="wsSheenMain" cx="36%" cy="20%" r="42%">
          <stop offset="0%" stopColor="rgba(255, 226, 208, 0.28)" />
          <stop offset="55%" stopColor="rgba(255, 218, 202, 0.045)" />
          <stop offset="100%" stopColor="rgba(255, 218, 202, 0)" />
        </radialGradient>
        <radialGradient id="wsSheenSpot" cx="30%" cy="18%" r="8%">
          <stop offset="0%" stopColor="rgba(255, 240, 225, 0.42)" />
          <stop offset="100%" stopColor="rgba(255, 240, 225, 0)" />
        </radialGradient>

        {/* Darker ring just inside the rim — sells the recessed centre. */}
        <radialGradient id="wsInner" cx="50%" cy="50%" r="46%">
          <stop offset="78%" stopColor="rgba(25, 5, 3, 0)" />
          <stop offset="100%" stopColor="rgba(25, 5, 3, 0.5)" />
        </radialGradient>

        {/* Orb fill — very slightly darker than the disc at the same spot,
         * so raised features feel like part of the wax, not pasted on. */}
        <linearGradient id="wsOrbFill" x1="22%" y1="16%" x2="82%" y2="90%">
          <stop offset="0%" stopColor="#AF4234" />
          <stop offset="50%" stopColor="#962F23" />
          <stop offset="100%" stopColor="#6A1F19" />
        </linearGradient>

        {/* Rim — seal-wide radial from the light source position, so the ring
         * is brighter on the top-left and darker on the bottom-right. */}
        <radialGradient id="wsRim" gradientUnits="userSpaceOnUse" cx="82" cy="62" r="180">
          <stop offset="0%" stopColor="#B84A3C" />
          <stop offset="45%" stopColor="#962F23" />
          <stop offset="100%" stopColor="#3F0C09" />
        </radialGradient>

        <clipPath id="wsClip">
          <path d={discPath} />
        </clipPath>
      </defs>

      {/* Base disc */}
      <path d={discPath} fill="url(#wsBody)" />

      {/* Surface texture: macro mottling + fine grain */}
      <g clipPath="url(#wsClip)">
        <rect x="0" y="0" width="220" height="220" filter="url(#wsMacro)" opacity="0.72" />
        <rect x="0" y="0" width="220" height="220" filter="url(#wsGrain)" opacity="0.55" />
      </g>

      {/* Recessed centre depth */}
      <g clipPath="url(#wsClip)">
        <circle cx="110" cy="110" r="86" fill="url(#wsInner)" />
      </g>

      {/* RIM — shadow behind, main ring, thin top-left highlight arcs, subtle inner edge */}
      <g clipPath="url(#wsClip)">
        <circle
          cx="110"
          cy="110"
          r="86"
          fill="none"
          stroke="#140303"
          strokeWidth="9"
          filter="url(#wsShadow)"
          opacity="0.68"
        />
        <circle cx="110" cy="110" r="86" fill="none" stroke="url(#wsRim)" strokeWidth="9" />
        <path
          d="M 54 48 A 80 80 0 0 1 168 58"
          fill="none"
          stroke="rgba(255, 208, 188, 0.42)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M 50 58 A 82 82 0 0 1 132 34"
          fill="none"
          stroke="rgba(255, 222, 204, 0.2)"
          strokeWidth="0.7"
          strokeLinecap="round"
        />
        <circle cx="110" cy="110" r="82" fill="none" stroke="rgba(18, 3, 3, 0.3)" strokeWidth="0.8" />
      </g>

      {/* LOGO — raised orbs with engraved bars + wavy-line detail */}
      <g clipPath="url(#wsClip)">
        <g transform="translate(65 60) scale(1.2)">
          {/* (1) cast shadow behind the raised orbs */}
          <g fill="#140303" filter="url(#wsShadow)" opacity="0.8">
            {orbs}
          </g>
          {/* (2) highlight layer — lighter wax offset up-left, peeks out on
               the top-left edges only; under the main fill */}
          <g transform="translate(-0.6 -0.7)" fill="#BE5346" opacity="0.88">
            {orbs}
          </g>
          {/* (3) main orb fill */}
          <g fill="url(#wsOrbFill)">{orbs}</g>

          {/* (4) engraved detail: dark recessed bars + wavy lines */}
          <g
            stroke="#2E0805"
            fill="#2E0805"
            filter="url(#wsDetailShadow)"
            opacity="0.92"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="10" y="20.4" width="20" height="2.8" rx="1.4" />
            <rect x="8" y="25.8" width="16" height="2.8" rx="1.4" />
            <path d="M 27 52 Q 37 46 47 52 T 67 54" fill="none" strokeWidth="2.6" />
            <path d="M 25 62 Q 37 56 47 62 T 69 62" fill="none" strokeWidth="2.6" />
            <path d="M 28 70 Q 38 65 48 70" fill="none" strokeWidth="2.6" />
          </g>
          {/* (5) thin warm highlight on the far edge of each engraved recess */}
          <g fill="rgba(200, 102, 82, 0.5)">
            <rect x="10" y="22.8" width="20" height="0.5" rx="0.25" />
            <rect x="8" y="28.2" width="16" height="0.5" rx="0.25" />
          </g>
          <g stroke="rgba(200, 102, 82, 0.45)" fill="none" strokeWidth="0.5" strokeLinecap="round">
            <path d="M 27 53.2 Q 37 47.2 47 53.2 T 67 55.2" />
            <path d="M 25 63.2 Q 37 57.2 47 63.2 T 69 63.2" />
            <path d="M 28 71.2 Q 38 66.2 48 71.2" />
          </g>
        </g>
      </g>

      {/* Overall upper-left pearl sheen */}
      <g clipPath="url(#wsClip)">
        <ellipse cx="86" cy="58" rx="82" ry="60" fill="url(#wsSheenMain)" />
        <ellipse cx="70" cy="46" rx="16" ry="10" fill="url(#wsSheenSpot)" />
      </g>
    </svg>
  );
}
